import { get, findLastIndex } from 'lodash';
import { SET_NEXT_RESUME_RESULT_ITEM } from './resumeResultActions';
import { SET_RESULT, CLEAR_RESULT, SET_LC_RESULT } from '../api/resultActions';
import {
    RCT, ACCURACY, COMPREHENSION, FLUENCY, LN, AR_SMALL,
} from '../../constants/measure';
import DNOServiceClient from '../clients/DNOServiceClient';
import TROServiceClient from '../clients/TROServiceClient';
import * as page from '../../constants/assessmentItemType';
import VocaboServiceClient from '../clients/VocaboServiceClient';
import SpellingServiceClient from '../clients/SpellingServiceClient';
import DecServiceClient from '../clients/DecServiceClient';
import { VR_RESULT_STATUS } from '../../constants/error';
import LCServiceClient from '../clients/LCServiceClient';

export const defaultState = {
    currentResults: {},
};

const getNextRCTAccuracyItem = (result) => result.measure_result.responses.length;

const getNextRCTFluCompItem = (result) => {
    const { measure_result: { passages } } = result;
    const lastAttemptedPassage = findLastIndex(passages, (passage) => passage.is_passage_complete);
    const nextPassage = lastAttemptedPassage + 1;
    return nextPassage === passages.length ? undefined : nextPassage;
};

const getNextTROItem = (result) => {
    const { measure_result: { book_results: bookResults }, is_complete: isComplete } = result;
    if (isComplete) {
        return undefined;
    }

    if (bookResults.length > 0) {
        const validBookResults = bookResults.filter(bookResult => bookResult.is_valid);
        const lastResult = validBookResults[validBookResults.length - 1];
        if (lastResult.vrr_result.status === VR_RESULT_STATUS.UNAVAILABLE) return page.BOOK;
        if (lastResult.passing_level === null && lastResult.vrr_result.status !== VR_RESULT_STATUS.FAILED) return page.COMPREHENSION;
        return page.BOOK;
    }
    return page.INSTRUCTIONS;
};

const getNextLCItem = (result) => {
    const { is_complete: isComplete } = result;
    if (isComplete) return undefined;

    return page.READING;
};

const getNextLNItem = (result) => {
    const { measure_result: { responses } } = result;
    if (responses.length > 0) {
        return responses.length + 1;
    }
    return undefined;
};

const getNextVocabItem = (result) => {
    const { measure_result: { responses } } = result;
    if (responses && responses.length >= 0) {
        return responses.length + 1;
    }
    return undefined;
};

const getNextSpellingItem = (result) => {
    const { measure_result: { responses } } = result;
    if (responses && responses.length >= 0) {
        return responses.length + 1;
    }
    return undefined;
};

const getNextWRItem = (result) => get(result, 'item_level_details.responses.length', 0) + 1;

const getNextItem = (result) => {
    // FIXME: convert DNO measure name to Lantern measure in setResult action,
    // so that we can avoid such convertion
    if (result.measure === DNOServiceClient.CLIENT_TO_SERVER_MEASURE_NAME[RCT]
        && result.measure_type === ACCURACY) {
        return getNextRCTAccuracyItem(result);
    } if (result.measure === DNOServiceClient.CLIENT_TO_SERVER_MEASURE_NAME[RCT]
        && [FLUENCY, COMPREHENSION].includes(result.measure_type)) {
        return getNextRCTFluCompItem(result);
    } if (result.measure === DNOServiceClient.CLIENT_TO_SERVER_MEASURE_NAME[LN.NAME]) {
        return getNextLNItem(result);
    } if (result.assessment === TROServiceClient.NAME ||
        result.assessment === AR_SMALL) {
        return getNextTROItem(result);
    } if (result.assessment === VocaboServiceClient.NAME) {
        return getNextVocabItem(result);
    } if (result.assessment === SpellingServiceClient.NAME) {
        return getNextSpellingItem(result);
    } if (result.assessment === DecServiceClient.NAME) {
        return getNextWRItem(result);
    } if (result.assessment === LCServiceClient.NAME) {
        return getNextLCItem(result);
    }
    return undefined;
};

const resumeReducer = ({ currentResults } = defaultState, action) => {
    switch (action.type) {
    case SET_LC_RESULT:
    case SET_RESULT:
        if (action.result.is_complete || getNextItem(action.result) === undefined) {
            return { currentResults };
        }
        return {
            currentResults: {
                ...currentResults,
                [action.result.result_id]: {
                    formId: action.result.form_id,
                    nextItem: getNextItem(action.result),
                },
            },
        };
    case SET_NEXT_RESUME_RESULT_ITEM:
        return {
            currentResults: Object.keys(currentResults).reduce((obj, resultId) => {
                if (action.resultId === resultId) {
                    return {
                        ...obj,
                        [resultId]: {
                            ...currentResults[resultId],
                            nextItem: action.nextItem,
                        },
                    };
                }
                return obj;
            }, currentResults),
        };
    /* eslint-disable no-unused-vars */
    case CLEAR_RESULT: {
        const { [action.resultId]: deletedProperty, ...newState } = currentResults;
        return {
            currentResults: newState,
        };
    }
    /* eslint-enable no-unused-vars */
    default:
        return { currentResults };
    }
};

export default resumeReducer;
